import { useEffect } from 'react';

/**
 * Refermer les notifications d'une conversation qu'on vient d'ouvrir — #77.
 *
 * ## Pourquoi ça vit dans la page et non dans le service worker
 *
 * `sw-push.js` supprime l'affichage AVANT de notifier : il inspecte les clients
 * visibles et bascule en `silent: true` quand la conversation est déjà à l'écran.
 * Sur le chemin déclaratif (#28) ce mécanisme n'existe plus — la plateforme
 * affiche elle-même, aucun code ne s'exécute au moment du push, il n'y a donc rien
 * à intercepter.
 *
 * La suppression change alors deux fois de nature : elle passe du service worker
 * **à l'app**, et d'avant l'affichage **à après**. `getNotifications({ tag })` puis
 * `close()` est le seul mécanisme disponible, et il est meilleur sur un point —
 * il nettoie aussi les notifications PÉRIMÉES, celles empilées sous le même `tag`
 * pendant que le téléphone était dans une poche.
 *
 * Le `tag` est écrit par le serveur (`WebPushService::construireChargeUtileMobile`)
 * sous la forme `tf-conversation-{customer_id}`. Les deux bouts doivent rester
 * d'accord : c'est le seul couplage de ce fichier.
 *
 * ## Entièrement défensif
 *
 * Aucune de ces API n'est garantie — pas de service worker en test, pas de
 * `getNotifications()` sur certains moteurs, permission jamais accordée. Rien ici
 * ne doit empêcher le fil de s'afficher : un échec est un silence, jamais une
 * exception qui remonte au rendu.
 */
export function useFermetureNotifications(customerId: string | undefined): void {
    useEffect(() => {
        if (!customerId) return;
        if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;

        // Le démontage peut précéder la résolution : on ne referme pas les
        // notifications d'une conversation qu'on a déjà quittée.
        let vivant = true;

        void navigator.serviceWorker
            .getRegistration()
            .then(async (registration) => {
                if (!vivant || !registration?.getNotifications) return;

                const notifications = await registration.getNotifications({
                    tag: `tf-conversation-${customerId}`,
                });
                notifications.forEach((notification) => notification.close());
            })
            .catch(() => {
                /* Pas de service worker, pas de permission, moteur sans l'API. */
            });

        return () => {
            vivant = false;
        };
    }, [customerId]);
}
